import { getDefaultBuConfig } from "domain/repository/apiUtil";
import moment from "moment-timezone";

// Cache environment variables at module load time [1]()
const config = {
  handleTimeZone: getDefaultBuConfig<string>('REACT_APP_HANDLE_TIMEZONE') === 'true',
  dateTimeFormat: getDefaultBuConfig<string>('REACT_APP_DATE_TIME_FORMAT') || 'dd/mm/yyyy HH:mm',
  timeFormat: getDefaultBuConfig<string>('REACT_APP_TIME_FORMAT') || 'HH:mm',
  dateFormat: getDefaultBuConfig<string>('REACT_APP_DATE_FORMAT') || 'dd/mm/yyyy',
};


// Cache system time zone to avoid repeated calculations [4]()
const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Converts Date to localized string using system time zone
 * @param date - Input Date object
 * @returns Localized date string in system format
 */
export const convertDateLocalFormat = (date: Date): string => {
  return date.toLocaleString(
    navigator.languages[0],
    {
      timeZone: systemTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }
  );
};

/**
 * Converts Date to UTC string if timezone handling enabled 
 * @param date - Input Date object
 * @returns UTC formatted string or default string
 */
export const convertDateUTCFormat = (date: Date): string => {
  if (config.handleTimeZone) {
    return date.toUTCString();
  } else {
    return date.toString();
  }
};

/**
 * Converts moment object to UTC moment
 * @param date - Input moment object 
 * @returns UTC moment object or original if invalid 
 */
export const convertDateToUTC = (date: moment.Moment): moment.Moment | undefined => {
  if (date.isValid()) {
    return config.handleTimeZone ? date.utc() : date;
  }
};

/**
 * Converts moment object to local time moment
 * @param date - Input moment object 
 * @returns Local moment object or original if invalid
 */
export const convertDateToLocal = (date: moment.Moment): moment.Moment | undefined => {
  if (date.isValid()) {
    return config.handleTimeZone ? date.local() : date;
  }
};

// Generic formatter function for component output 
const formatDate = (date: any, format: string): string | undefined => {
  if (date) {
    const m = moment.isMoment(date) ? date : moment(date);
    return m.isValid() ? m.format(format) : undefined;
  }
};

/**
 * Formats date to date-time string for UI components 
 * @param date - Input date value 
 * @returns Formatted date-time string 
 */
export const convertDateToDateTimeForComponent = (date: any): string | undefined => {
  return formatDate(date, config.dateTimeFormat);
};

/**
 * Formats date to date-only string for UI components 
 * @param date - Input date value
 * @returns Formatted date string
 */
export const convertDateToDateForComponent = (date: any): string | undefined => {
  return formatDate(date, config.dateFormat);
};

/**
 * Formats date to time-only string for UI components
 * @param date - Input date value 
 * @returns Formatted time string 
 */
export const convertDateToTimeForComponent = (date: any): string | undefined => {
  return formatDate(date, config.timeFormat);
};

// Additional utility functions 

/**
 * Gets the user's time zone with fallback to UTC 
 * @returns Detected time zone string 
 */
export const getUserTimeZone = (): string => {
  try {
    return systemTimeZone;
  } catch {
    return 'UTC';
  }
};

/**
 * Formats date to localized string with language support
 * @param date - Input moment object 
 * @param locale - Language code (e.g. 'zh-CN')
 * @returns Localized date string
 */
export const formatLocalizedDate = (date: moment.Moment, locale: string): string | null => {
  return date?.isValid() ? date.locale(locale).format('LLL') : null;
};

export const convertDateToDateStr = (date: any) =>
  formatDate(date, config.dateFormat);

export const convertDateToDateTimeStr = (date: any) =>
  formatDate(date, config.dateTimeFormat);

export const convertDateToTimeStr = (date: any) =>
  formatDate(date, config.timeFormat);

/**
 * Parse date to ISO string
 * @param params 
 * @returns ISO string
 */
export const dateParser = (params: { oldValue: any; newValue: any }): string | null => {
  const value = params.newValue;
  if (!value) return null;

  // Handle ISO format with timezone (e.g., 2015-09-30T16:00:00.000+00:00)
  // Parse with moment and convert to local timezone
  if (typeof value === 'string') {
    // Check if it's an ISO format with timezone
    const isoWithTimezone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    if (isoWithTimezone.test(value)) {
      const m = moment(value);
      if (m.isValid()) {
        // Convert to local timezone and format
        return m.local().toISOString();
      }
    }

    // Check if already in simple ISO format (YYYY-MM-DD)
    const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/;
    if (isoDateOnly.test(value)) {
      return value;
    }

    const m = moment(value, config.dateFormat, true);
    if (m.isValid()) {
      return m.toISOString();
    }
  }
  return value;
}

/**
 * Parse date time to ISO string
 * @param params 
 * @returns ISO string
 */
export const dateTimeParser = (params: { oldValue: any; newValue: any }): string | null => {
  const value = params.newValue;
  if (!value) return null;

  // Handle ISO format with timezone (e.g., 2015-09-30T16:00:00.000+00:00)
  // Parse with moment and convert to local timezone
  if (typeof value === 'string') {
    // Check if it's an ISO format with timezone
    const isoWithTimezone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    if (isoWithTimezone.test(value)) {
      const m = moment(value);
      if (m.isValid()) {
        // Convert to local timezone and format
        return m.local().toISOString();
      }
    }

    const m = moment(value, config.dateTimeFormat, true);
    if (m.isValid()) {
      return m.toISOString();
    }
  }
  return value;
}